import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import {
  NEGOCIO_MAX_FOTOS,
  emptyNegocioForm,
  negocioFormFromListing,
  negocioFotoPublicUrl,
} from '../../lib/negocioHelpers';

const emptyItem = () => ({
  nombre: '',
  descripcion: '',
  precio: '',
  foto_url: null,
  _file: null,
  _preview: null,
});

export default function NegocioFormModal({
  open,
  listing = null,
  onClose,
  onSubmit,
}) {
  const isEdit = !!listing;
  const [form, setForm] = useState(emptyNegocioForm());
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingFotos, setExistingFotos] = useState([]);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(negocioFormFromListing(listing));
    setLogoFile(null);
    setRemoveLogo(false);
    setGalleryFiles([]);
    setExistingFotos(listing?.fotos || []);
    setLogoPreview(listing?.logo_url ? negocioFotoPublicUrl(listing.logo_url) : null);
    setError('');
    setSaving(false);
  }, [open, listing]);

  if (!open) return null;

  const totalGallery = existingFotos.length + galleryFiles.length;
  const canAddGallery = totalGallery < NEGOCIO_MAX_FOTOS;

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setRemoveLogo(false);
    setLogoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = NEGOCIO_MAX_FOTOS - existingFotos.length - galleryFiles.length;
    if (room <= 0) return;
    const accepted = files.slice(0, room).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setGalleryFiles((prev) => [...prev, ...accepted]);
  };

  const removeExistingFoto = (path) => {
    setExistingFotos((prev) => prev.filter((p) => p !== path));
  };

  const removeGalleryFile = (idx) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));

  const updateItem = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleItemFoto = (idx, file) => {
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === idx
          ? { ...item, _file: file, _preview: URL.createObjectURL(file) }
          : item
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (form.contacto_tipo !== 'ninguno' && !form.contacto_valor.trim()) {
      setError('Completá el dato de contacto o elegí “Ninguno”');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSubmit({
        form,
        logoFile,
        removeLogo,
        existingLogoUrl: listing?.logo_url || null,
        galleryFiles: galleryFiles.map((g) => g.file),
        existingFotos,
        listingId: listing?.id || null,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        className="relative bg-white rounded-[32px] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 md:p-8 border-b border-stone-100 bg-[#FAF9F6] flex items-start justify-between gap-4 shrink-0">
          <div>
            <h3 className="font-editorial font-bold text-2xl text-[#1B2A4E]">
              {isEdit ? 'Editar Negocio o Servicio' : 'Agregar Negocio o Servicio'}
            </h3>
            <p className="text-sm text-stone-500 mt-1">
              Quedará pendiente de aprobación antes de publicarse.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-[#1B2A4E] p-2">
            <SafeIcon name="X" className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6 overflow-y-auto">
          <div>
            <label className="block label-technical text-stone-400 mb-2">Tipo</label>
            <div className="flex gap-3">
              {['negocio', 'servicio'].map((tipo) => (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => updateField('tipo', tipo)}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm border transition-colors ${
                    form.tipo === tipo
                      ? 'bg-[#1B2A4E] text-white border-[#1B2A4E]'
                      : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-[#74ACDF]'
                  }`}
                >
                  {tipo === 'negocio' ? 'Negocio' : 'Servicio'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block label-technical text-stone-400 mb-2">Nombre *</label>
            <input
              required
              value={form.nombre}
              onChange={(e) => updateField('nombre', e.target.value)}
              className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]"
              placeholder="Ej: Panadería Los Andes"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block label-technical text-stone-400 mb-2">Categoría</label>
              <input
                value={form.categoria}
                onChange={(e) => updateField('categoria', e.target.value)}
                className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]"
                placeholder="Gastronomía, Legal…"
              />
            </div>
            <div>
              <label className="block label-technical text-stone-400 mb-2">Ubicación</label>
              <input
                value={form.ubicacion}
                onChange={(e) => updateField('ubicacion', e.target.value)}
                className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]"
                placeholder="Miami, FL"
              />
            </div>
          </div>

          <div>
            <label className="block label-technical text-stone-400 mb-2">Descripción</label>
            <textarea
              rows={3}
              value={form.descripcion}
              onChange={(e) => updateField('descripcion', e.target.value)}
              className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]"
              placeholder="Contá qué ofrecés…"
            />
          </div>

          <div>
            <label className="block label-technical text-stone-400 mb-2">Logo / foto principal</label>
            <div className="flex items-center gap-4">
              {logoPreview && !removeLogo ? (
                <div className="relative">
                  <img src={logoPreview} alt="" className="w-20 h-20 rounded-2xl object-cover border border-stone-200" />
                  <button
                    type="button"
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreview(null);
                      setRemoveLogo(true);
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <SafeIcon name="X" className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="w-20 h-20 rounded-2xl border-2 border-dashed border-stone-300 text-stone-400 flex items-center justify-center hover:border-[#74ACDF] hover:text-[#74ACDF]"
                >
                  <SafeIcon name="Image" className="w-6 h-6" />
                </button>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="text-sm font-bold text-[#74ACDF] hover:text-[#1B2A4E]"
                >
                  {logoPreview && !removeLogo ? 'Cambiar imagen' : 'Subir imagen'}
                </button>
                <p className="text-xs text-stone-400 mt-1">JPG o PNG, se ve en la portada pública</p>
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-technical text-stone-400">Galería de fotos</label>
              <span className={`text-xs font-bold ${totalGallery >= NEGOCIO_MAX_FOTOS ? 'text-red-500' : 'text-stone-400'}`}>
                {totalGallery}/{NEGOCIO_MAX_FOTOS}
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {existingFotos.map((path) => (
                <div key={path} className="relative">
                  <img
                    src={negocioFotoPublicUrl(path)}
                    alt=""
                    className="w-20 h-20 rounded-xl object-cover border border-stone-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeExistingFoto(path)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <SafeIcon name="X" className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {galleryFiles.map((g, idx) => (
                <div key={g.preview} className="relative">
                  <img src={g.preview} alt="" className="w-20 h-20 rounded-xl object-cover border border-stone-200" />
                  <button
                    type="button"
                    onClick={() => removeGalleryFile(idx)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <SafeIcon name="X" className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {canAddGallery && (
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-stone-300 text-stone-400 flex items-center justify-center hover:border-[#74ACDF]"
                >
                  <SafeIcon name="Plus" className="w-5 h-5" />
                </button>
              )}
            </div>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleGalleryChange}
              disabled={!canAddGallery}
            />
          </div>

          {form.tipo === 'negocio' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="label-technical text-stone-400">Productos / Servicios</label>
                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm font-bold text-[#74ACDF] hover:text-[#1B2A4E] flex items-center gap-1"
                >
                  <SafeIcon name="Plus" className="w-4 h-4" /> Agregar ítem
                </button>
              </div>
              {form.items.length === 0 && (
                <p className="text-sm text-stone-400 italic">Opcional: listá lo que vendés u ofrecés.</p>
              )}
              {form.items.map((item, idx) => (
                <div key={idx} className="p-4 rounded-2xl border border-stone-200 bg-stone-50 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-stone-400">Ítem {idx + 1}</span>
                    <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600">
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    value={item.nombre}
                    onChange={(e) => updateItem(idx, 'nombre', e.target.value)}
                    placeholder="Nombre"
                    className="w-full p-3 rounded-xl bg-white border border-stone-200 outline-none text-[#1B2A4E] text-sm"
                  />
                  <textarea
                    rows={2}
                    value={item.descripcion}
                    onChange={(e) => updateItem(idx, 'descripcion', e.target.value)}
                    placeholder="Descripción"
                    className="w-full p-3 rounded-xl bg-white border border-stone-200 outline-none text-[#1B2A4E] text-sm"
                  />
                  <div className="flex gap-3 items-center">
                    <input
                      value={item.precio}
                      onChange={(e) => updateItem(idx, 'precio', e.target.value)}
                      placeholder="Precio (opcional)"
                      className="flex-1 p-3 rounded-xl bg-white border border-stone-200 outline-none text-[#1B2A4E] text-sm"
                    />
                    <label className="shrink-0 cursor-pointer text-sm font-bold text-[#74ACDF] flex items-center gap-1">
                      <SafeIcon name="Image" className="w-4 h-4" />
                      Foto
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleItemFoto(idx, e.target.files?.[0])}
                      />
                    </label>
                    {(item._preview || item.foto_url) && (
                      <img
                        src={item._preview || negocioFotoPublicUrl(item.foto_url)}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block label-technical text-stone-400 mb-2">Contacto</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { id: 'whatsapp', label: 'WhatsApp' },
                { id: 'link', label: 'Link' },
                { id: 'ninguno', label: 'Ninguno' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => updateField('contacto_tipo', opt.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold border transition-colors ${
                    form.contacto_tipo === opt.id
                      ? 'bg-[#1B2A4E] text-white border-[#1B2A4E]'
                      : 'bg-white text-stone-600 border-stone-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {form.contacto_tipo !== 'ninguno' && (
              <input
                value={form.contacto_valor}
                onChange={(e) => updateField('contacto_valor', e.target.value)}
                className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-[#1B2A4E]"
                placeholder={
                  form.contacto_tipo === 'whatsapp'
                    ? '+13055551234'
                    : 'https://tu-sitio.com'
                }
              />
            )}
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{error}</div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#1B2A4E] text-white font-bold py-4 rounded-2xl hover:bg-[#253969] transition-colors shadow-lg disabled:opacity-60"
          >
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Enviar a revisión'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
